import React from "react";
import { FiCode, FiFileText, FiGrid, FiList } from "react-icons/fi";

type PageEditorViewControlsProps = {
	contentViewMode: "blocks" | "json" | "markdown";
	isJsonLocked: boolean;
	pageJsonError: string | null;
	onSelectBlocks: () => void;
	onSelectJson: () => void;
	onSelectMarkdown: () => void;
	pageNavOpen: boolean;
	onTogglePageNav: () => void;
};

/**
 * The page editor's own half of the action bar: the page-tree toggle and the Blocks / Markdown / JSON
 * switcher. Passed to EditorActionBar as its `leadingSlot`; the content-data editor has neither of
 * these, which is the only intended difference between the two bars.
 */
const PageEditorViewControls: React.FC<PageEditorViewControlsProps> = ({
	contentViewMode,
	isJsonLocked,
	pageJsonError,
	onSelectBlocks,
	onSelectJson,
	onSelectMarkdown,
	pageNavOpen,
	onTogglePageNav
}) => {
	// Sized in clamp() like the rest of the bar, so it scales with the viewport instead of stepping at
	// breakpoints; below 1150px the tabs keep their icons and drop their words, because the bar must
	// stay on one line.
	const tab = (active: boolean) =>
		`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-[clamp(0.35rem,0.6vw,0.625rem)] py-[clamp(0.15rem,0.35vw,0.25rem)] text-[clamp(0.65rem,0.75vw,0.75rem)] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
			active
				? "bg-slate-800 text-white shadow-sm"
				: "text-slate-600 hover:bg-white hover:text-slate-900"
		}`;
	const LABEL = "max-[1150px]:hidden";

	return (
		<div className="flex flex-nowrap items-center gap-2">
			<button
				type="button"
				onClick={onTogglePageNav}
				className={`inline-flex shrink-0 items-center justify-center rounded-full border px-[clamp(0.35rem,0.6vw,0.625rem)] py-[clamp(0.15rem,0.35vw,0.25rem)] transition ${
					pageNavOpen
						? "border-slate-300 bg-slate-100 text-slate-700"
						: "border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-600"
				}`}
				title={pageNavOpen ? "Masquer l'arborescence des pages" : "Afficher l'arborescence des pages"}
				aria-label={pageNavOpen ? "Masquer l'arborescence des pages" : "Afficher l'arborescence des pages"}
			>
				<FiList className="h-3.5 w-3.5" />
			</button>
			<div className="flex flex-nowrap items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-0.5">
				<button
					type="button"
					disabled={isJsonLocked}
					onClick={onSelectBlocks}
					title="Blocs"
					className={tab(contentViewMode === "blocks")}
				>
					<FiGrid className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>Blocs</span>
				</button>
				<button type="button" onClick={onSelectMarkdown} title="Markdown" className={tab(contentViewMode === "markdown")}>
					<FiFileText className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>Markdown</span>
				</button>
				<button type="button" onClick={onSelectJson} title="JSON" className={tab(contentViewMode === "json")}>
					<FiCode className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>JSON</span>
				</button>
			</div>
			{contentViewMode === "json" && pageJsonError && (
				<span className="shrink-0 whitespace-nowrap text-[11px] font-semibold text-rose-600">JSON invalide</span>
			)}
		</div>
	);
};

export default PageEditorViewControls;
